import { useEffect, useRef, useState } from "react";
import { IconClose } from "./icons";
import { getGateway } from "../payments/gateway";
import styles from "./PaymentOverlay.module.css";

// Generic payment overlay. Looks up the configured gateway, asks the
// BFF for a payment intent, then mounts the gateway widget into a
// portal-style card on top of the page. Stripe / Bolt / future gateway
// all surface through this same shell — only the inner widget changes.
export default function PaymentOverlay({
  open,
  gatewayId,
  intentRequest,            // { orderId, amount, currency, capture, customer, items, metadata }
  onSuccess,
  onError,
  onClose,
}) {
  const containerRef = useRef(null);
  const gatewayRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !gatewayId || !intentRequest) return;
    let cancelled = false;
    setPhase("loading");
    setError(null);
    (async () => {
      try {
        const g = getGateway(gatewayId);
        gatewayRef.current = g;
        await g.loadSdk?.();
        if (cancelled) return;
        const intent = await g.createIntent(intentRequest);
        if (cancelled) return;
        await g.mount({
          container: containerRef.current,
          token: intent.token,
          intent,
          capture: !!intentRequest.capture,
          on: {
            success: (tx) => onSuccess?.({ ...tx, intent }),
            error: (e) => { setError(e?.message || "Payment failed"); onError?.(e); },
            close: () => onClose?.(),
            ready: () => setPhase("ready"),
          },
        });
        if (!cancelled) setPhase("ready");
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Could not start payment");
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      try { gatewayRef.current?.unmount?.(); } catch { /* */ }
    };
  }, [open, gatewayId, intentRequest, onSuccess, onError, onClose]);

  if (!open) return null;

  return (
    <div className={styles.scrim} role="dialog" aria-modal="true" aria-label="Payment">
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div>
            <div className={styles.eyebrow}>Secure Payment</div>
            <div className={styles.title}>
              {gatewayRef.current?.label || gatewayId || "Payment"}
              {intentRequest?.capture === false && <span className={styles.tag}>Auth only</span>}
            </div>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close payment">
            <IconClose />
          </button>
        </header>
        <div className={styles.body}>
          {phase === "loading" && <div className={styles.loading}>Starting checkout…</div>}
          {phase === "error" && (
            <div className={styles.error}>
              <strong>Payment unavailable</strong>
              <p>{error}</p>
            </div>
          )}
          <div ref={containerRef} className={styles.mount} />
        </div>
      </div>
    </div>
  );
}
